import { VALHALLA_BASE_URL } from '../constants';

/**
 * Valhalla map-matching + routing service.
 *
 * Two strategies depending on gap size:
 *   Short gaps (<30s): trace_route (map-match / snap existing GPS to roads)
 *   Long gaps (30s–5min): route (full turn-by-turn driving route between points)
 *
 * Both return dense road geometry that gets converted into evenly-spaced
 * waypoints so the vehicle drives smoothly along roads — no teleporting.
 */

const LONG_GAP_THRESHOLD_MS = 30000; // 30s — switch from trace_route to route

// ── Build Valhalla request bodies ───────────────────────────────────────────

function buildTraceRequest(points, options = {}) {
  const shape = points.map((p, i) => {
    const entry = {
      lat: p.lat,
      lon: p.lng,
      type: i === 0 ? 'break' : 'via',
    };
    if (p.timestamp != null && points[0].timestamp != null) {
      entry.time = (p.timestamp - points[0].timestamp) / 1000;
    }
    if (p.heading != null && p.heading >= 0) {
      entry.heading = p.heading;
      entry.heading_tolerance = options.headingTolerance || 45;
    }
    entry.radius = options.gpsAccuracy || 30;
    return entry;
  });

  if (shape.length > 0) {
    shape[shape.length - 1].type = 'break';
  }

  return {
    shape,
    costing: options.costing || 'auto',
    shape_match: 'map_snap',
    filters: {
      attributes: [
        'shape', 'matched.point', 'matched.type', 'matched.edge_index',
        'edge.speed', 'edge.speed_limit', 'edge.road_class',
        'edge.begin_heading', 'edge.end_heading', 'edge.length',
      ],
      action: 'include',
    },
  };
}

function buildRouteRequest(startPt, endPt, options = {}) {
  const locations = [
    {
      lat: startPt.lat,
      lon: startPt.lng,
      type: 'break',
      heading: startPt.heading != null && startPt.heading >= 0 ? startPt.heading : undefined,
      heading_tolerance: 45,
    },
    {
      lat: endPt.lat,
      lon: endPt.lng,
      type: 'break',
      heading: endPt.heading != null && endPt.heading >= 0 ? endPt.heading : undefined,
      heading_tolerance: 45,
    },
  ];

  return {
    locations,
    costing: options.costing || 'auto',
    directions_options: { units: 'miles' },
  };
}

// ── Polyline decoder (Valhalla precision 6) ─────────────────────────────────

function decodePolyline(encoded, precision = 6) {
  const factor = Math.pow(10, precision);
  const points = [];
  let lat = 0, lng = 0, index = 0;

  while (index < encoded.length) {
    let shift = 0, result = 0, byte;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    shift = 0; result = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    points.push({ lat: lat / factor, lng: lng / factor });
  }
  return points;
}

// ── Geo helpers ─────────────────────────────────────────────────────────────

function deg2rad(deg) { return deg * (Math.PI / 180); }
function rad2deg(rad) { return rad * (180 / Math.PI); }

function bearing(from, to) {
  const dLng = deg2rad(to.lng - from.lng);
  const fromLat = deg2rad(from.lat);
  const toLat = deg2rad(to.lat);
  const y = Math.sin(dLng) * Math.cos(toLat);
  const x = Math.cos(fromLat) * Math.sin(toLat) -
    Math.sin(fromLat) * Math.cos(toLat) * Math.cos(dLng);
  return (rad2deg(Math.atan2(y, x)) + 360) % 360;
}

function haversineMeters(a, b) {
  const R = 6371000;
  const dLat = deg2rad(b.lat - a.lat);
  const dLng = deg2rad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat +
    Math.cos(deg2rad(a.lat)) * Math.cos(deg2rad(b.lat)) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function cumulativeDistances(path) {
  const dists = [0];
  for (let i = 1; i < path.length; i++) {
    dists.push(dists[i - 1] + haversineMeters(path[i - 1], path[i]));
  }
  return dists;
}

// ── trace_route (map-match for short gaps) ──────────────────────────────────

export async function mapMatch(points, options = {}) {
  if (!points || points.length < 2) return null;

  const body = buildTraceRequest(points, options);
  const url = `${VALHALLA_BASE_URL}/trace_route`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.warn('Valhalla trace_route failed:', response.status);
      return null;
    }
    const data = await response.json();
    return parseResult(data);
  } catch (err) {
    console.warn('Valhalla trace_route error:', err.message);
    return null;
  }
}

// ── route (driving directions for long gaps) ────────────────────────────────

export async function valhallaRoute(startPt, endPt, options = {}) {
  const body = buildRouteRequest(startPt, endPt, options);
  const url = `${VALHALLA_BASE_URL}/route`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.warn('Valhalla route failed:', response.status);
      return null;
    }
    const data = await response.json();
    return parseResult(data);
  } catch (err) {
    console.warn('Valhalla route error:', err.message);
    return null;
  }
}

// ── Shared response parser ──────────────────────────────────────────────────

function parseResult(data) {
  if (!data.trip || !data.trip.legs || data.trip.legs.length === 0) return null;

  const leg = data.trip.legs[0];
  const roadShape = decodePolyline(leg.shape);

  const edges = (leg.maneuvers || []).map(m => ({
    beginShapeIndex: m.begin_shape_index,
    endShapeIndex: m.end_shape_index,
    speedLimit: m.speed_limit || null,
    roadClass: m.road_class || null,
    beginHeading: m.begin_heading,
    endHeading: m.end_heading,
    length: m.length,
  }));

  const distances = cumulativeDistances(roadShape);
  const totalDistance = distances[distances.length - 1];

  const headings = roadShape.map((pt, i) => {
    if (i < roadShape.length - 1) return bearing(pt, roadShape[i + 1]);
    return i > 0 ? bearing(roadShape[i - 1], pt) : 0;
  });

  const speeds = roadShape.map((_, i) => {
    const edge = edges.find(e => i >= e.beginShapeIndex && i <= e.endShapeIndex);
    if (edge && edge.speedLimit) return edge.speedLimit * 0.44704; // → m/s
    return null;
  });

  return { roadShape, distances, totalDistance, headings, speeds, edges };
}

// ── Match confidence scoring ────────────────────────────────────────────────

/**
 * Score how well a Valhalla match fits the actual GPS evidence.
 * Returns a confidence value from 0.0 (bad match) to 1.0 (perfect match).
 *
 * Catches: parking lots, U-turns, side streets, off-road, outdated maps.
 *
 * Three independent checks, combined:
 *   1. Endpoint proximity — does route end near GPS endpoint?
 *   2. Distance ratio — is route length reasonable vs straight-line?
 *   3. Heading agreement — does GPS heading match road direction?
 *
 * @param {Object} matchResult - Output of mapMatch() or valhallaRoute()
 * @param {Object} startPt - GPS start {lat, lng, heading}
 * @param {Object} endPt - GPS end {lat, lng, heading}
 * @returns {number} Confidence 0.0–1.0
 */
export function scoreMatchConfidence(matchResult, startPt, endPt) {
  if (!matchResult || !matchResult.roadShape || matchResult.roadShape.length < 2) {
    return 0;
  }

  const { roadShape, totalDistance, headings } = matchResult;
  const straightDist = haversineMeters(startPt, endPt);

  // ── 1. Endpoint proximity ─────────────────────────────────────────────
  // How close does the route's last point land to the actual GPS endpoint?
  // Within 30m = perfect, >200m = bad match.
  const routeEnd = roadShape[roadShape.length - 1];
  const routeStart = roadShape[0];
  const endDrift = haversineMeters(routeEnd, endPt);
  const startDrift = haversineMeters(routeStart, startPt);
  const maxDrift = Math.max(endDrift, startDrift);

  let endpointScore;
  if (maxDrift <= 30) endpointScore = 1.0;
  else if (maxDrift <= 100) endpointScore = 1.0 - (maxDrift - 30) / 140; // linear 1→0.5
  else if (maxDrift <= 200) endpointScore = 0.5 - (maxDrift - 100) / 200; // linear 0.5→0
  else endpointScore = 0;

  // ── 2. Distance ratio ─────────────────────────────────────────────────
  // Route distance vs straight-line. A ratio of 1.0 = direct, >3.0 = suspicious.
  // For very short distances (<20m), skip this check — noise dominates.
  let distanceScore = 1.0;
  if (straightDist > 20) {
    const ratio = totalDistance / straightDist;
    if (ratio <= 1.5) distanceScore = 1.0;        // normal road curvature
    else if (ratio <= 3.0) distanceScore = 1.0 - (ratio - 1.5) / 3.0; // degrading
    else if (ratio <= 5.0) distanceScore = 0.5 - (ratio - 3.0) / 4.0; // bad
    else distanceScore = 0;                        // route is wildly indirect
  }

  // ── 3. Heading agreement ──────────────────────────────────────────────
  // Compare GPS heading at start/end to the road heading at those points.
  // If the vehicle says "going west" but the road says "going east", bail.
  let headingScore = 1.0;
  const roadStartHeading = headings[0];
  const roadEndHeading = headings[headings.length - 1];

  if (startPt.heading != null && startPt.heading >= 0 && roadStartHeading != null) {
    const startHdiff = Math.abs(angleDiffSigned(startPt.heading, roadStartHeading));
    if (startHdiff <= 30) headingScore = Math.min(headingScore, 1.0);
    else if (startHdiff <= 60) headingScore = Math.min(headingScore, 0.8);
    else if (startHdiff <= 90) headingScore = Math.min(headingScore, 0.5);
    else headingScore = Math.min(headingScore, 0.2); // driving opposite direction
  }

  if (endPt.heading != null && endPt.heading >= 0 && roadEndHeading != null) {
    const endHdiff = Math.abs(angleDiffSigned(endPt.heading, roadEndHeading));
    if (endHdiff <= 30) headingScore = Math.min(headingScore, 1.0);
    else if (endHdiff <= 60) headingScore = Math.min(headingScore, 0.8);
    else if (endHdiff <= 90) headingScore = Math.min(headingScore, 0.5);
    else headingScore = Math.min(headingScore, 0.2);
  }

  // ── Combined confidence ───────────────────────────────────────────────
  // Weight: endpoint proximity matters most, then heading, then distance.
  const confidence = endpointScore * 0.4 + headingScore * 0.35 + distanceScore * 0.25;
  return Math.max(0, Math.min(1, confidence));
}

// Signed shortest angular difference
function angleDiffSigned(a, b) {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

// ── Gap detection (time-based) ──────────────────────────────────────────────

/**
 * Returns the gap duration in milliseconds between two consecutive GPS points.
 * Returns 0 if no meaningful gap exists.
 *
 * @param {Object} prev - Previous point {timestamp}
 * @param {Object} curr - Current point {timestamp}
 * @param {number} minGapMs - Minimum duration to be considered a gap
 * @returns {number} Gap duration in ms (0 = no gap)
 */
export function detectGapMs(prev, curr, minGapMs) {
  if (!prev || !curr) return 0;
  const timeDelta = curr.timestamp - prev.timestamp;
  return timeDelta >= minGapMs ? timeDelta : 0;
}

// ── Road-aware gap filling ──────────────────────────────────────────────────

/**
 * Fill a gap between two GPS points with road-following waypoints.
 *
 * Makes ONE Valhalla call to get the road geometry, then places waypoints
 * at regular intervals (waypointIntervalMs) along the road. For a 5-minute
 * gap at 3s intervals, this generates ~100 smooth road-following waypoints.
 *
 * Uses trace_route for short gaps (<30s) and route for longer gaps.
 *
 * @param {Object} prevPoint - Start of gap {lat, lng, speed, heading, timestamp}
 * @param {Object} newPoint - End of gap {lat, lng, speed, heading, timestamp}
 * @param {number} gapDurationMs - Duration of the gap in ms
 * @param {number} waypointIntervalMs - Spacing between generated waypoints (e.g., 3000)
 * @returns {Promise<Array>} Waypoints [{lat, lng, heading, speed, timestamp, isInterpolated}]
 */
export async function fillGap(prevPoint, newPoint, gapDurationMs, waypointIntervalMs) {
  // How many intermediate waypoints to generate
  const count = Math.max(1, Math.floor(gapDurationMs / waypointIntervalMs) - 1);

  // Pick the right Valhalla endpoint based on gap length
  let matchResult;
  if (gapDurationMs <= LONG_GAP_THRESHOLD_MS) {
    // Short gap: map-match (trace_route)
    matchResult = await mapMatch([prevPoint, newPoint], {
      gpsAccuracy: 25,
      headingTolerance: 45,
    });
  } else {
    // Long gap: full route
    matchResult = await valhallaRoute(prevPoint, newPoint);
  }

  if (!matchResult || !matchResult.roadShape || matchResult.roadShape.length < 2) {
    return [];
  }

  return generateRoadWaypoints(matchResult, prevPoint, newPoint, count);
}

// ── Waypoint generator ──────────────────────────────────────────────────────

/**
 * Given a Valhalla road result, place `count` intermediate waypoints evenly
 * along the road geometry with interpolated timestamps, road-derived headings,
 * and speed from road speed limits or distance/time estimation.
 */
export function generateRoadWaypoints(matchResult, startPoint, endPoint, count) {
  if (!matchResult || !matchResult.roadShape || matchResult.roadShape.length < 2 || count < 1) {
    return [];
  }

  const { roadShape, distances, totalDistance, headings, speeds } = matchResult;
  const startTimestamp = startPoint.timestamp;
  const endTimestamp = endPoint.timestamp;
  const duration = endTimestamp - startTimestamp;
  const startSpeed = startPoint.speed || 0;
  const endSpeed = endPoint.speed || 0;

  const waypoints = [];

  for (let i = 1; i <= count; i++) {
    const fraction = i / (count + 1);
    const targetDist = fraction * totalDistance;
    const targetTime = startTimestamp + fraction * duration;

    // Find the road segment that brackets this distance
    let segIdx = 0;
    for (let j = 1; j < distances.length; j++) {
      if (distances[j] >= targetDist) {
        segIdx = j - 1;
        break;
      }
      if (j === distances.length - 1) segIdx = j - 1;
    }

    // Interpolate within that road segment
    const segStart = distances[segIdx];
    const segEnd = distances[segIdx + 1] || segStart + 1;
    const segFraction = segEnd > segStart ? (targetDist - segStart) / (segEnd - segStart) : 0;
    const p0 = roadShape[segIdx];
    const p1 = roadShape[segIdx + 1] || p0;

    const lat = p0.lat + (p1.lat - p0.lat) * segFraction;
    const lng = p0.lng + (p1.lng - p0.lng) * segFraction;

    // Heading from road tangent
    const heading = headings[segIdx] != null ? headings[segIdx] : bearing(p0, p1);

    // Speed: prefer road speed limit, then blend start/end GPS speeds
    const roadSpeed = speeds[segIdx];
    let speedMph;
    if (roadSpeed != null) {
      speedMph = roadSpeed * 2.23694; // m/s → mph
    } else if (duration > 0) {
      // Blend between start and end speed based on position along route
      speedMph = startSpeed + (endSpeed - startSpeed) * fraction;
      // If both are 0 (stopped→stopped), estimate from distance/time
      if (speedMph <= 0 && totalDistance > 0) {
        speedMph = (totalDistance / (duration / 1000)) * 2.23694;
      }
    } else {
      speedMph = 0;
    }

    waypoints.push({
      lat,
      lng,
      heading,
      speed: Math.max(0, speedMph),
      timestamp: targetTime,
      isInterpolated: true,
    });
  }

  return waypoints;
}

export { decodePolyline, haversineMeters, cumulativeDistances, bearing };
